import { AxiosResponse } from "axios";
import { ClientConfiguration } from "../models/ClientConfiguration/ClientConfiguration";
import { SharepointApp } from "../models/ClientConfiguration/SharepointApp";
import { extractApiErrorMessage } from "../lib/apiError";
import useApi from "../services/ApiService";

interface GetClientConfigurationsQueryOptions {
  ids?: string[];
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
}

export class ClientConfigurationRepository {
  private api = useApi();

  async getClientConfiguration(id: string): Promise<ClientConfiguration> {
    try {
      const response: AxiosResponse<ClientConfiguration> = await this.api.get(
        `/api/ClientConfigurations/${id}`,
      );
      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(`Failed to fetch client configuration with id: ${id}`);
    }
  }

  async getClientConfigurations(
    organizationId: string,
    options: GetClientConfigurationsQueryOptions = {},
  ): Promise<ClientConfiguration[]> {
    const {
      ids = null,
      pageNumber = null,
      pageSize = null,
      orderBy = "Name",
      descending = false,
    } = options;

    try {
      const response: AxiosResponse<ClientConfiguration[]> = await this.api.get(
        "/api/ClientConfigurations",
        {
          params: {
            OrganizationId: organizationId,
            Id: ids,
            PageNumber: pageNumber,
            PageSize: pageSize,
            OrderBy: orderBy,
            Descending: descending,
          },
        },
      );

      return response.data;
    } catch (error) {
      // Handle error
      throw new Error("Failed to fetch client configurations");
    }
  }

  async getSharepointApps(): Promise<SharepointApp[]> {
    try {
      const response: AxiosResponse<SharepointApp[]> = await this.api.get(
        "/api/ClientConfigurations/SharepointApps",
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch SharePoint apps");
    }
  }

  async updateClientConfiguration(
    id: string,
    updatedConfiguration: ClientConfiguration,
    reason?: string
  ): Promise<ClientConfiguration> {
    try {
      const response: AxiosResponse<ClientConfiguration> = await this.api.put(`/api/ClientConfigurations/${id}`, {
        configurationPatch: updatedConfiguration,
        reason: reason
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createClientConfiguration(
    newConfiguration: ClientConfiguration,
    reason?: string
  ): Promise<ClientConfiguration> {
    try {
      const { id, dateCreated, ...newConfigPayload } = newConfiguration;
      const response: AxiosResponse<ClientConfiguration> = await this.api.post(`/api/ClientConfigurations/`,{
        clientConfiguration: newConfigPayload,
        reason: reason
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  async deleteConfig(configId: string, replacementConfigId?: string | null, newDefaultConfigId?: string | null): Promise<void> {
    try {
      return await this.api.delete(`/api/ClientConfigurations/${configId}`, {
        data: {
          replacementConfigurationId: replacementConfigId ?? null,
          newDefaultConfigurationId: newDefaultConfigId ?? null,
        },
      });
    } catch (error) {
      // Handle error
      throw new Error("Failed to delete configuration");
    }
  }

  async setDefaultConfig(configId: string, reason?: string): Promise<void> {
    try {
      return await this.api.patch(`/api/ClientConfigurations/SetAsDefaultConfiguration`, {
        configurationId: configId,
        reason: reason,
      });
    } catch (error) {
      // Handle error
      throw new Error("Failed to set configuration as default");
    }
  }
}

export default ClientConfigurationRepository;
